import { Route, Navigate, Routes, Outlet } from 'react-router-dom';
import { useState } from 'react';
import { LicenseInfo } from '@mui/x-date-pickers-pro';
import { useSelector } from 'react-redux';
import {
    ResetPassword,
    BasePage,
    Coverages,
    Design,
    ModelsPage,
    Manufacturers,
    Dashboard,
    Login,
    Locations,
    Issues,
    Users,
    Products,
    Resellers,
    ResellersSecondary,
    CutActivity,
    CutHistory,
    ExportActivity,
    BatchProcessing,
    Organizations,
    InvoiceManagement,
    EmployeeDetails,
    BillingChanges,
    StationMoveActivity,
    AuditHistory,
    AdminAuditLog,
    MaterialProductManagement,
} from '@pages';
import { dateFormat, getMonthInfo } from '@utils/dates';
import { isAdmin } from '@utils/permissions';
import { AppRoutes } from '@consts/routes';
import { Settings, loadSetting, saveSetting } from '@utils/localStorage';
import './App.css';

import type { RootState } from '@features/redux/store';
import * as Sentry from '@sentry/react';
import { Redirect } from './helpers/Redirect';

import { User } from '@pages/manage';

import { BreadcrumbSubBar } from '@components/BreadcrumbSubBar';
import { TodaysCuts } from '@pages/TodaysCuts';
import { Employees } from '@pages/Employees';

import { TicketsLanding } from '@pages/tickets/Landing/TicketsLanding';
import { Tickets } from '@pages/tickets/TicketForm/Tickets';

LicenseInfo.setLicenseKey(import.meta.env.VITE_MUI_LICENSE_KEY);

export const App = Sentry.withProfiler(
    ({ SentryRoutes }) => {
        const { firstDay } = getMonthInfo(1);

        const defaultRange = loadSetting(Settings.DateRange) ?? {
            startDate: dateFormat(firstDay),
            endDate: dateFormat(),
        };

        const [dateRange, setDateRange] = useState<{
            startDate: string;
            endDate: string;
        }>(defaultRange);

        const updateRange = (range: { startDate: string; endDate: string }) => {
            setDateRange(range);
            saveSetting(Settings.DateRange, range);
        };

        const { IdToken } = useSelector((state: RootState) => state.auth);

        const showTips = IdToken ? !isAdmin(IdToken) : false;

        return (
            <SentryRoutes>
                {/* Redirect non-existing routes to /insights */}
                <Route path='*' element={<Navigate to={AppRoutes.Insights} replace />} />

                {/* Unauthenticated routes */}
                <Route path={AppRoutes.Login} element={IdToken ? <Redirect to={AppRoutes.Insights} /> : <Login />} />
                <Route path={AppRoutes.ResetPassword} element={<ResetPassword />} />
                <Route path={AppRoutes.Tickets} element={<TicketsLanding />} />
                <Route path={AppRoutes.TicketForm} element={<Tickets />} />

                {/* Admin routes */}
                <Route path={AppRoutes.AdminAuditLog} element={<AdminAuditLog />} />
                <Route path={AppRoutes.MaterialProductManagement} element={<MaterialProductManagement />} />

                {/* Authenticated routes */}
                <Route
                    path={AppRoutes.Insights}
                    element={<Dashboard setDateRange={updateRange} dateRange={dateRange} showTips={showTips} />}
                />

                <Route
                    path={AppRoutes.Locations}
                    element={<Locations setDateRange={updateRange} dateRange={dateRange} showTips={showTips} />}
                />

                <Route
                    path={AppRoutes.Products}
                    element={<Products setDateRange={updateRange} dateRange={dateRange} showTips={showTips} />}
                />

                <Route path={AppRoutes.TodaysCuts} element={<TodaysCuts />} />

                <Route
                    path={AppRoutes.Resellers}
                    element={<Resellers setDateRange={updateRange} dateRange={dateRange} showTips={showTips} />}
                />

                <Route
                    path={AppRoutes.ResellersSecondary}
                    element={
                        <ResellersSecondary setDateRange={updateRange} dateRange={dateRange} showTips={showTips} />
                    }
                />

                <Route
                    path={AppRoutes.Issues}
                    element={<Issues setDateRange={updateRange} dateRange={dateRange} showTips={showTips} />}
                />

                <Route
                    path={AppRoutes.Manufacturers}
                    element={
                        <BasePage>
                            <Manufacturers />
                        </BasePage>
                    }
                />

                <Route
                    path={AppRoutes.Models}
                    element={
                        <BasePage>
                            <ModelsPage />
                        </BasePage>
                    }
                />

                <Route
                    path={AppRoutes.Design}
                    element={
                        <BasePage>
                            <Design />
                        </BasePage>
                    }
                />

                <Route
                    path={AppRoutes.Coverages}
                    element={
                        <BasePage>
                            <Coverages />
                        </BasePage>
                    }
                />

                <Route
                    path={AppRoutes.Activity}
                    element={
                        <BasePage noPadding>
                            <CutActivity />
                        </BasePage>
                    }
                />
                {/* only for usage as a window.open popup */}
                <Route path={AppRoutes.ExportActivity} element={<ExportActivity />} />

                <Route path={AppRoutes.EmployeeDetails} element={<EmployeeDetails />} />
                <Route path={AppRoutes.BillingChanges} element={<BillingChanges />} />
                <Route path={AppRoutes.StationMoveActivity} element={<StationMoveActivity />} />

                <Route
                    path={AppRoutes.AuditTrail}
                    element={
                        <BasePage>
                            <AuditHistory />
                        </BasePage>
                    }
                />

                <Route
                    path={AppRoutes.Users}
                    element={
                        <BasePage>
                            <Users />
                        </BasePage>
                    }
                />

                <Route path={AppRoutes.CutHistory} element={<CutHistory />} />
                <Route path={AppRoutes.BatchProcessing} element={<BatchProcessing />} />
                <Route path={AppRoutes.Invoices} element={<InvoiceManagement />} />

                <Route path={AppRoutes.Organizations} element={<Organizations />} />

                <Route
                    path={AppRoutes.ManageUser}
                    element={
                        <BasePage>
                            <BreadcrumbSubBar />
                            <div style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                                <User />
                            </div>
                        </BasePage>
                    }
                />

                <Route
                    path={AppRoutes.Employees}
                    element={<Employees setDateRange={updateRange} dateRange={dateRange} />}
                />
            </SentryRoutes>
        );
    },
    {
        name: 'InsightsFrontEnd',
        includeRender: true,
        includeUpdates: true,
    }
);
